import type {
  BacktestCreateRequest,
  BacktestRunDto,
  BacktestMarkerDto,
  BacktestReplaySummaryDto,
  ReplayEventDto,
  ReplayPageDto,
  ReplayTradeDto,
  StrategyDocumentDto,
  StrategySummaryDto,
} from './types';
import { api } from './client';

export class BacktestsService {
  async list(signal?: AbortSignal): Promise<BacktestRunDto[]> {
    return api.get<BacktestRunDto[]>('/api/backtests', { signal });
  }

  async get(id: string, signal?: AbortSignal): Promise<BacktestRunDto> {
    return api.get<BacktestRunDto>(`/api/backtests/${encodeURIComponent(id)}`, { signal });
  }

  async create(req: BacktestCreateRequest): Promise<BacktestRunDto> {
    return api.post<BacktestRunDto>('/api/backtests', req);
  }

  async getStrategySnapshot(id: string, signal?: AbortSignal): Promise<StrategyDocumentDto> {
    return api.get<StrategyDocumentDto>(
      `/api/backtests/${encodeURIComponent(id)}/strategy-snapshot`,
      { signal },
    );
  }

  async getReplaySummary(id: string, signal?: AbortSignal): Promise<BacktestReplaySummaryDto> {
    return api.get<BacktestReplaySummaryDto>(`/api/backtests/${encodeURIComponent(id)}/replay/summary`, { signal });
  }

  async getReplayEvents(
    id: string,
    request: { before?: string | null; after?: string | null; limit?: number; kind?: string; search?: string },
    signal?: AbortSignal,
  ): Promise<ReplayPageDto<ReplayEventDto>> {
    return api.get<ReplayPageDto<ReplayEventDto>>(
      replayUrl(id, 'events', request),
      { signal },
    );
  }

  async getReplayEvent(id: string, eventId: string, signal?: AbortSignal): Promise<ReplayEventDto> {
    return api.get<ReplayEventDto>(
      `/api/backtests/${encodeURIComponent(id)}/replay/events/${encodeURIComponent(eventId)}`,
      { signal },
    );
  }

  async getReplayTrades(
    id: string,
    request: { before?: string | null; after?: string | null; limit?: number; direction?: string; outcome?: string },
    signal?: AbortSignal,
  ): Promise<ReplayPageDto<ReplayTradeDto>> {
    return api.get<ReplayPageDto<ReplayTradeDto>>(
      replayUrl(id, 'trades', request),
      { signal },
    );
  }

  async getReplayTrade(id: string, tradeId: string, signal?: AbortSignal): Promise<ReplayTradeDto> {
    return api.get<ReplayTradeDto>(
      `/api/backtests/${encodeURIComponent(id)}/replay/trades/${encodeURIComponent(tradeId)}`,
      { signal },
    );
  }

  async getReplayMarkers(
    id: string,
    request: { from: string; to: string; kind?: string; limit?: number },
    signal?: AbortSignal,
  ): Promise<BacktestMarkerDto[]> {
    return api.get<BacktestMarkerDto[]>(replayUrl(id, 'markers', request), { signal });
  }

  async cloneStrategySnapshot(id: string, name?: string): Promise<StrategySummaryDto> {
    return api.post<StrategySummaryDto>(
      `/api/backtests/${encodeURIComponent(id)}/strategy-snapshot/clone`,
      { name },
    );
  }
}

function replayUrl(id: string, resource: string, request: Record<string, string | number | null | undefined>) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(request)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const query = params.toString();
  return `/api/backtests/${encodeURIComponent(id)}/replay/${resource}${query ? `?${query}` : ''}`;
}

export const backtests = new BacktestsService();
